import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import classNames from 'classnames';
import s from './Tooltip.module.scss';

export interface TooltipProps {
  content: React.ReactNode;
  children: React.ReactElement;
  className?: string;
  align?: 'center' | 'right' | 'left' | 'auto';
}

export const Tooltip = ({ content, children, className, align = 'auto' }: TooltipProps) => {
  const [visible, setVisible] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0 });
  const [effAlign, setEffAlign] = useState<'center' | 'right' | 'left'>('center');
  const triggerRef = useRef<HTMLElement>(null);

  const updateCoords = () => {
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      const resolvedAlign = align === 'auto' ? (rect.left > window.innerWidth - 180 ? 'right' : 'center') : align;
      setEffAlign(resolvedAlign);

      if (resolvedAlign === 'right') {
        setCoords({
          top: rect.bottom + window.scrollY + 8,
          left: rect.right + window.scrollX,
        });
      } else if (resolvedAlign === 'left') {
        setCoords({
          top: rect.bottom + window.scrollY + 8,
          left: rect.left + window.scrollX,
        });
      } else {
        setCoords({
          top: rect.bottom + window.scrollY + 8,
          left: rect.left + window.scrollX + rect.width / 2,
        });
      }
    }
  };

  useEffect(() => {
    if (visible) {
      updateCoords();
      window.addEventListener('scroll', updateCoords, true);
      window.addEventListener('resize', updateCoords);
      const handleDocClick = (e: MouseEvent) => {
        if (triggerRef.current && !triggerRef.current.contains(e.target as Node)) {
          setVisible(false);
        }
      };
      document.addEventListener('click', handleDocClick);
      return () => {
        window.removeEventListener('scroll', updateCoords, true);
        window.removeEventListener('resize', updateCoords);
        document.removeEventListener('click', handleDocClick);
      };
    }
  }, [visible, align]);

  const child = React.cloneElement(children, {
    ref: triggerRef,
    onMouseEnter: (e: any) => {
      setVisible(true);
      if (children.props.onMouseEnter) children.props.onMouseEnter(e);
    },
    onMouseLeave: (e: any) => {
      setVisible(false);
      if (children.props.onMouseLeave) children.props.onMouseLeave(e);
    },
    onClick: (e: any) => {
      e.stopPropagation();
      setVisible((prev) => !prev);
      if (children.props.onClick) children.props.onClick(e);
    },
  });

  const alignClass = effAlign === 'right' ? s.alignRight : effAlign === 'left' ? s.alignLeft : s.alignCenter;

  return (
    <>
      {child}
      {visible &&
        createPortal(
          <div className={classNames(s.tooltip, alignClass, className)} style={{ top: coords.top, left: coords.left }}>
            {content}
          </div>,
          document.body,
        )}
    </>
  );
};
